// src/utils/dateTimeUtils.js
//
// Conversions date/heure "murale" (heure de Bruxelles, telle que saisie par
// l'admin) ↔ instant UTC réel, DST-safe et indépendantes du fuseau horaire
// du navigateur qui exécute ce code.
//
// ⚠️ Fichier partagé : CourseForm.js (création de cours) ET CourseEdit.js
// (édition) doivent tous les deux importer depuis ici plutôt que de garder
// leur propre copie. Historique : ces deux formulaires avaient chacun leur
// version de cette logique, elles ont divergé, et CourseForm.js s'est
// retrouvé avec une version buguée (±2h codés en dur au lieu d'un calcul
// dynamique) pendant que CourseEdit.js avait déjà la bonne version. D'où ce
// fichier unique, pour que ça ne puisse plus se reproduire.

const EUROPE_BRUSSELS_TZ = 'Europe/Brussels';

function getTimeZoneOffsetAt(utcMs, timeZone) {
  const d = new Date(utcMs);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(d);
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  const asGMT = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return (asGMT - utcMs) / 60000;
}

/// Convertit une date "YYYY-MM-DD" + heure "HH:mm" saisies par l'admin,
/// entendues comme heure de Bruxelles, en ISO string UTC réel. Gère
/// automatiquement l'heure d'été/hiver (double vérification de l'offset
/// pour les cas limites au moment exact du changement d'heure).
export function toBelgiumUTCISO(dateStr, timeStr) {
  if (!dateStr) return '';
  const [Y, M, D] = dateStr.split('-').map(Number);
  const [h, m]   = (timeStr || '00:00').split(':').map(Number);
  const naiveUTC = Date.UTC(Y, (M || 1) - 1, D || 1, h || 0, m || 0, 0);
  let off = getTimeZoneOffsetAt(naiveUTC, EUROPE_BRUSSELS_TZ);
  let utcMs = naiveUTC - off * 60000;
  const off2 = getTimeZoneOffsetAt(utcMs, EUROPE_BRUSSELS_TZ);
  if (off2 !== off) utcMs = naiveUTC - off2 * 60000;
  return new Date(utcMs).toISOString();
}

/// Variante pour un input HTML "datetime-local" (ex: "2026-08-30T10:00").
export function datetimeLocalToBelgiumUTCISO(dtLocal) {
  if (!dtLocal) return '';
  const [d, t] = dtLocal.split('T');
  return toBelgiumUTCISO(d, t || '00:00');
}

/// Affiche un instant (ISO string ou Firestore Timestamp) en heure de
/// Bruxelles, formaté en français.
export function formatForBelgiumDisplay(isoOrTs) {
  if (!isoOrTs) return '';
  const d = isoOrTs && typeof isoOrTs.toDate === 'function' ? isoOrTs.toDate() : new Date(isoOrTs);
  return new Intl.DateTimeFormat('fr-BE', {
    timeZone: EUROPE_BRUSSELS_TZ,
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

/// Retourne { ymd: 'YYYY-MM-DD', hm: 'HH:mm' } en heure de Bruxelles à
/// partir d'une date ISO/Firestore. Sert à pré-remplir les champs
/// date/heure d'un formulaire d'édition à partir d'une valeur déjà stockée.
export function isoToBelgiumParts(isoOrTs) {
  if (!isoOrTs) return { ymd: '', hm: '' };
  const d = isoOrTs && typeof isoOrTs.toDate === 'function' ? isoOrTs.toDate() : new Date(isoOrTs);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: EUROPE_BRUSSELS_TZ,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(d).map(p => [p.type, p.value]));
  return {
    ymd: `${parts.year}-${parts.month}-${parts.day}`,
    hm: `${parts.hour}:${parts.minute}`,
  };
}
